/**
 * 事務局専用のイベント種別業務フロー（D-282）。
 * 社外に出さない運用手順のため、ログイン＋事務局ロールでのみ表示する。
 */

export type OpsPlace = 'screen' | 'real' | 'both';

export const OPS_PLACE_LABELS: Record<OpsPlace, string> = {
  screen: '画面',
  real: 'リアル',
  both: '両方',
};

export interface OpsFlowStep {
  seq: number;
  who: 'office' | 'student' | 'director' | 'success' | 'both';
  whoLabel: string;
  when: string;
  where: string;
  what: string;
  /** 管理画面上の操作か、対面・手元・電話などのリアル対応か */
  place: OpsPlace;
  why: string;
  /** この操作で生まれるデータ／画面を、誰がいつから見られるか */
  visibility?: string;
  /** この行の前後で飛ぶ自動メール（種別プリセット。無ければ省略＝表では —） */
  auto?: string;
}

/** 種別横断の「誰が・いつから見られるか」早見（D-282） */
export const VISIBILITY_GUIDE: { topic: string; detail: string }[] = [
  {
    topic: 'イベント自体',
    detail:
      '下書き＝事務局のみ。公開後＝対象受講生・担当者（理事／サクセス）・事務局。受講生の一覧には自分が対象の回だけ出る。',
  },
  {
    topic: '会員プロフィール',
    detail:
      '公開項目（氏名・会社・役職・期・コース・顔写真・自己紹介など）は受講生同士でも相互閲覧可。電話・連絡先メールは本人と事務局のみ。理事・サクセスは担当範囲の会員詳細（公開項目中心）。',
  },
  {
    topic: '課題・アンケートの回答',
    detail:
      '提出した本人は常に自分の回答を見られる。事務局は個別回答を確認可。理事・サクセスは担当会員分の生回答を確認可（集計の公開範囲はテンプレの設定による。標準は理事・事務局向け。ナレッジシェア等は「参加者同士」もある）。',
  },
  {
    topic: '当日写真',
    detail:
      'アップロードした瞬間から、事務局・その回の担当者・対象受講生が「当日写真」タブで見られる（下書きイベントは事務局のみ）。面談（サクセス／1on1）では使わない。',
  },
  {
    topic: '席次',
    detail:
      'システムでは扱わない（D-284）。事務局が出欠一覧のCSV（出欠を取らない回は対象者名簿）を書き出し、手元の表計算ソフトで作る。受講生の画面に卓は出ない。',
  },
  {
    topic: '優秀者・態度フィードバック',
    detail:
      '入力した理事・講師と事務局が確認。採点の生データ材料になる。受講生向けの個別通知画面ではない。',
  },
  {
    topic: '面談記録（サクセス面談）',
    detail: 'サクセス担当と事務局のみ。理事・講師・受講生には公開されない。',
  },
  {
    topic: 'グループメッセージ',
    detail: 'その回のグループ参加者（対象受講生・担当者）と事務局。公開後にグループが作られる。',
  },
];

export interface OpsFlowSection {
  id: string;
  title: string;
  lead: string;
  note?: string;
  pending?: boolean;
  steps: OpsFlowStep[];
}

export const EVENT_OPS_FLOW_SECTIONS: OpsFlowSection[] = [
  {
    id: 'monthly',
    title: '① 月例会',
    lead: '標準の集合イベント。出欠は取りません（欠席は事務局への個別連絡）。席次もシステムでは扱わず、対象者名簿をもとに手元で作ります。タブは「事前課題 → 当日アウトプット → 事後アンケート → 社長ミーティングフィードバック」の順です。',
    steps: [
      {
        seq: 1,
        who: 'office',
        whoLabel: '事務局',
        when: '開催決定時',
        where: 'イベント管理 → 月例会 → 「月例会の新規登録」',
        what: '日時・会場・定員（設定しない／人数を指定）・参加形式を入れ、下書きで作成する',
        place: 'screen',
        why: '以降の課題・通知の土台を作る（月例会に出欠の設定は出ない）',
        auto: '作成時に配信予定を生成（公開前は送信時刻でもスキップ）',
      },
      {
        seq: 2,
        who: 'office',
        whoLabel: '事務局',
        when: '作成直後',
        where: 'イベント詳細 → 対象者／担当者',
        what: '期・コースで対象受講生を確定し、担当の理事・講師を付ける',
        place: 'screen',
        why: '誰に見えるか・誰が会員情報を見られるかを決める',
      },
      {
        seq: 3,
        who: 'office',
        whoLabel: '事務局',
        when: '同上',
        where: 'イベント詳細 → 課題・アンケートの各タブ',
        what: 'テンプレートから事前課題・当日アウトプット・事後アンケート・社長ミーティングフィードバックを割当（事後アンケートの期限目安＝翌月10日）',
        place: 'screen',
        why: '受講生の提出物と督促の対象を用意する',
      },
      {
        seq: 4,
        who: 'office',
        whoLabel: '事務局',
        when: '準備完了時',
        where: 'イベント詳細 → 公開する',
        what: '公開する（名簿固定・連絡グループが同時にできる）',
        place: 'screen',
        why: '受講生に見せ、案内メールの配信予約を有効にする',
        visibility: 'この時点から対象受講生・担当者にイベントが見える。グループメッセージも使える',
        auto: '公開で予約が有効に。毎月10日09:00・受講生全員へまとめ配信（テーブルNo.1・ランキング・課題リマインド）',
      },
      {
        seq: 5,
        who: 'office',
        whoLabel: '事務局',
        when: 'オンライン時',
        where: 'イベント詳細 → 概要を編集 → 会議URL ／ Meet本体',
        what: 'rec@cxo-growth.jp でMeetを発行・録画予約し、URLを画面に登録する',
        place: 'both',
        why: '参加案内と参加ログ取得の前提をそろえる',
        visibility: '対象受講生・担当者はイベント詳細の概要でURLを見られる（公開後）',
      },
      {
        seq: 6,
        who: 'student',
        whoLabel: '受講生',
        when: '公開後〜期限まで',
        where: 'イベント詳細 → 事前課題',
        what: '事前課題を提出する',
        place: 'screen',
        why: '当日の議論の材料にする',
        visibility: '本人・事務局・担当者が回答を確認可',
      },
      {
        seq: 7,
        who: 'office',
        whoLabel: '事務局',
        when: '当日運営の準備時',
        where: 'イベント詳細 → 対象者（名簿） ／ Excel等（お手元）',
        what: '対象者の名簿をもとに、席次表・当日の受付名簿を手元で作る',
        place: 'both',
        why: '席次はシステムで扱わない（D-284）。卓割付は手元の表計算ソフトで行う',
        visibility: '手元で作った席次表は、当日の配布やグループメッセージへの添付で伝える',
      },
      {
        seq: 8,
        who: 'student',
        whoLabel: '受講生',
        when: '当日',
        where: '会場 ＋ イベント詳細 → 当日アウトプット',
        what: '開催に参加し、会場でその場の「当日アウトプット」に回答する',
        place: 'both',
        why: '開催直後の学び・気づきを残す',
        visibility:
          '提出後：本人は自分の回答を見られる。事務局・担当理事は個別回答／集計を確認可',
        auto: '開催前日09:00・参加者／理事へ前日案内',
      },
      {
        seq: 9,
        who: 'director',
        whoLabel: '理事・講師',
        when: '翌朝',
        where: '担当イベント → 当日写真',
        what: '当日写真をアップロードする',
        place: 'screen',
        why: '対象受講生も振り返れるようにする',
        visibility:
          '登録した瞬間から、事務局・担当者・対象受講生が「当日写真」タブで見られる',
        auto: '開催翌日09:00・理事へ写真アップロード依頼',
      },
      {
        seq: 10,
        who: 'student',
        whoLabel: '受講生',
        when: '開催後〜翌月10日',
        where: 'イベント詳細 → 事後アンケート',
        what: '開催後の提出物（事後アンケート）を提出する',
        place: 'screen',
        why: '実践内容を残し、採点・振り返りの材料にする',
        visibility: '本人・事務局・担当者が回答を確認可',
        auto: '開催翌日09:00・参加者へ案内（期限の目安＝翌月10日）',
      },
      {
        seq: 11,
        who: 'student',
        whoLabel: '受講生',
        when: '社長ミーティング後',
        where: 'イベント詳細 → 社長ミーティングフィードバック',
        what: '割当がある回では、社長ミーティングフィードバックを提出する',
        place: 'screen',
        why: '社長との場の振り返りを残す',
        visibility: '本人・事務局・担当者が回答を確認可',
      },
      {
        seq: 12,
        who: 'office',
        whoLabel: '事務局',
        when: '採点期',
        where: '採点 → 採点管理（CSV・公開） ／ お手元の集計シート',
        what: '生データ出力→手元集計→結果CSV取込→公開',
        place: 'both',
        why: '順位・評価を受講生に開示する',
        visibility: '採点「公開」後：受講生・理事・サクセスが順位一覧を見られる。内訳は本人と事務局のみ',
      },
    ],
  },
  {
    id: 'subcommittee',
    title: '② 分科会',
    lead: '定員目安20名。定員は先着順で締め、埋まった時点で受講生は参加を選べません（キャンセル待ちには回りません / D-336）。受講生はアウトプットを開催翌日まで提出し、理事・講師は開催5日後に優秀者・態度を入力します（D-281）。',
    steps: [
      {
        seq: 1,
        who: 'office',
        whoLabel: '事務局',
        when: '枠決定時',
        where: 'イベント管理 → 分科会 → 「分科会の新規登録」',
        what: 'テーマ・定員（人数を指定。目安20）・出欠を取るか・対象・担当を設定し、事前アンケート・事後アンケート・アウトプットを割当（期限＝開催翌日）',
        place: 'screen',
        why: '少人数回の運営と提出物の土台を作る。定員を入れると出欠は先着順で締まる',
        auto: '作成時に配信予定を生成（公開前はスキップ）',
      },
      {
        seq: 2,
        who: 'office',
        whoLabel: '事務局',
        when: '準備完了時',
        where: 'イベント詳細 → 公開する',
        what: '公開する（連絡グループ作成）',
        place: 'screen',
        why: '受講生への案内と事前アンケート依頼を開始する',
        visibility: '公開後：対象受講生・担当理事にイベントとグループが見える',
        auto: 'アンケート開放時・参加者へ開催案内。開催14日前09:00・理事リマインド',
      },
      {
        seq: 3,
        who: 'office',
        whoLabel: '事務局',
        when: 'オンライン時',
        where: 'Meet本体 ＋ イベント詳細 → 概要の会議URL',
        what: 'Meet発行・録画予約し、URLを画面に登録する',
        place: 'both',
        why: '当日参加と録画の前提をそろえる',
        visibility: '公開後、対象受講生・担当者が概要でURLを見られる',
      },
      {
        seq: 4,
        who: 'student',
        whoLabel: '受講生',
        when: '公開後〜出欠の締切まで',
        where: 'イベント → 該当回 → 出欠',
        what: '参加を申請する（先着順。定員に達すると選べなくなる）',
        place: 'screen',
        why: '募集人数を先着で締める（キャンセル待ちには回さない / D-336）',
        visibility:
          '事務局は出欠一覧で全員分を見られる。受講生は自分の申請のみ。人数の調整は事務局が「変更」で行う',
      },
      {
        seq: 5,
        who: 'student',
        whoLabel: '受講生',
        when: '期限まで',
        where: 'イベント詳細 → 事前アンケート',
        what: '講師への質問を事前に回答する',
        place: 'screen',
        why: '当日の議論の材料を理事・講師に渡す',
        visibility: '本人・事務局・担当理事が回答を確認可',
        auto: '事前アンケート期限当日09:00・未回答者のみ督促',
      },
      {
        seq: 6,
        who: 'both',
        whoLabel: '両者',
        when: '前日〜当日',
        where: '会場またはオンライン会議',
        what: '分科会を実施する',
        place: 'real',
        why: '学びの場そのもの',
        auto: '開催前日09:00・参加者へ前日案内（事後アンケート・アウトプット予告）',
      },
      {
        seq: 7,
        who: 'student',
        whoLabel: '受講生',
        when: '開催翌日まで',
        where: 'イベント詳細 → アウトプット',
        what: '学びと自社での実践をアウトプット提出する',
        place: 'screen',
        why: '優秀者選定の材料にし、実践を促す',
        visibility: '提出後：本人・事務局・担当理事が読める（優秀者画面でも共有表示）',
      },
      {
        seq: 8,
        who: 'student',
        whoLabel: '受講生',
        when: '開催後',
        where: 'イベント詳細 → 事後アンケート',
        what: '事後アンケートに回答する',
        place: 'screen',
        why: '役立ち度・改善点を残す',
        visibility: '本人・事務局・担当理事が確認可。開催5日後の優秀者依頼で理事にも共有',
      },
      {
        seq: 9,
        who: 'director',
        whoLabel: '理事・講師',
        when: '開催5日後〜',
        where: '担当イベント → 優秀者選定・態度フィードバック',
        what: '画面上の事後アンケート・アウトプットを見ながら、優秀者（最大3名）と態度を入力する',
        place: 'screen',
        why: '採点の評価材料にする（メッセンジャー回収の置き換え）',
        visibility: '入力内容は事務局と当該理事が確認。受講生向けの公開画面ではない',
        auto: '開催5日後09:00・理事へ優秀者申告依頼。依頼から2日後・未提出なら督促',
      },
      {
        seq: 10,
        who: 'director',
        whoLabel: '理事・講師',
        when: '（任意）',
        where: '担当イベント → 当日写真',
        what: '当日写真をアップロードする',
        place: 'screen',
        why: '振り返り用の記録を残す',
        visibility: '登録直後から事務局・担当者・対象受講生が「当日写真」で見られる',
      },
    ],
  },
  {
    id: 'exseminar',
    title: '③ EXゼミ',
    lead: '課題シートとナレッジシェア・事後アンケートが中心。テーブル分けが必要なときは、対象者名簿（出欠を取る回は出欠一覧のCSV）をもとに手元で作ります。',
    steps: [
      {
        seq: 1,
        who: 'office',
        whoLabel: '事務局',
        when: '開催決定時',
        where: 'イベント管理 → EXゼミ → 「EXゼミの新規登録」',
        what: '対象期・担当を設定し、課題シート・ナレッジシェア・事後アンケートを割当',
        place: 'screen',
        why: '提出物と当日運営の土台を作る',
        auto: '作成時に配信予定を生成（公開前はスキップ）',
      },
      {
        seq: 2,
        who: 'office',
        whoLabel: '事務局',
        when: '準備完了時',
        where: 'イベント詳細 → 公開する',
        what: '公開する',
        place: 'screen',
        why: '提出依頼とグループ連絡を開始する',
        auto: 'アンケート開放時・参加者へ提出依頼。開催14日前09:00・理事リマインド',
      },
      {
        seq: 3,
        who: 'office',
        whoLabel: '事務局',
        when: 'オンライン時',
        where: 'Meet本体 ＋ イベント詳細 → 会議URL',
        what: 'Meet発行・URL登録',
        place: 'both',
        why: 'オンライン参加の案内をそろえる',
      },
      {
        seq: 4,
        who: 'office',
        whoLabel: '事務局',
        when: '公開後（任意）',
        where: 'イベント詳細 → 対象者（名簿）／出欠 ＋ Excel等（お手元）',
        what:
          'テーブル分けが必要なら、名簿（出欠を取る回は「出欠一覧をCSV出力」）をもとに手元で作り、当日の配布やグループメッセージで伝える（③-3）',
        place: 'both',
        why: '当日の座席を伝える（席次はシステムでは扱わない / D-284）',
      },
      {
        seq: 5,
        who: 'student',
        whoLabel: '受講生',
        when: '各期限まで',
        where: 'イベント詳細 → 事前課題',
        what: '課題シート・ナレッジシェアを提出する',
        place: 'screen',
        why: '事前学習とテーブル内共有の材料にする',
        visibility:
          '課題シート：本人・事務局・担当。ナレッジシェア：参加受講生同士でも閲覧可（event_participants）',
        auto: '提出期限当日09:00・未提出者のみ督促',
      },
      {
        seq: 6,
        who: 'both',
        whoLabel: '両者',
        when: '前日〜当日',
        where: '会場またはオンライン会議',
        what: 'EXゼミを実施する',
        place: 'real',
        why: '学びの場そのもの',
        auto: '開催前日09:00・参加者へ前日案内',
      },
      {
        seq: 7,
        who: 'student',
        whoLabel: '受講生',
        when: '開催後',
        where: 'イベント詳細 → 事後アンケート',
        what: '事後アンケートに回答する',
        place: 'screen',
        why: '振り返りと改善点を残す',
        visibility: '本人・事務局・担当理事が確認可',
      },
      {
        seq: 8,
        who: 'director',
        whoLabel: '理事・講師',
        when: '開催後',
        where: '担当イベント → 事後アンケート（結果）',
        what: '事後アンケート結果を確認する（優秀者選定はなし）',
        place: 'screen',
        why: '次回運営の改善材料にする',
        visibility: '担当理事・事務局が結果を閲覧（開催翌日メールでも案内）',
        auto: '開催翌日09:00・理事へ事後アンケート結果の共有',
      },
    ],
  },
  {
    id: 'networking',
    title: '④ 交流会',
    lead: '運用詳細はヒアリング未確定のため、システムでできる最小フローのみ記載しています。',
    pending: true,
    note: '④-3〜④-7の回答後に行を足してください。',
    steps: [
      {
        seq: 1,
        who: 'office',
        whoLabel: '事務局',
        when: '開催決定時',
        where: 'イベント管理 → 交流会 → 「交流会の新規登録」',
        what: '日時・対象者・担当を設定して作成する',
        place: 'screen',
        why: '案内・出欠の受け皿を作る',
        auto: '交流会専用プリセットなし（④-7で設計）',
      },
      {
        seq: 2,
        who: 'office',
        whoLabel: '事務局',
        when: '準備完了時',
        where: 'イベント詳細 → 公開する',
        what: '公開する',
        place: 'screen',
        why: '受講生に見せ、出欠や案内を始められるようにする',
      },
      {
        seq: 3,
        who: 'office',
        whoLabel: '事務局',
        when: 'オンライン時',
        where: 'Meet本体 ＋ イベント詳細 → 会議URL',
        what: 'Meet発行・URL登録',
        place: 'both',
        why: '参加手段をそろえる',
      },
      {
        seq: 4,
        who: 'student',
        whoLabel: '受講生',
        when: '公開後',
        where: 'イベント → 該当回 → 出欠',
        what: '出欠を取る運用にする場合はここで申請する',
        place: 'screen',
        why: '参加者数を把握する（④-3で確定）',
      },
      {
        seq: 5,
        who: 'office',
        whoLabel: '事務局',
        when: '随時',
        where: 'イベント詳細 → 通知／グループ または メール・電話等',
        what: '必要な案内を送る（交流会専用プリセットは未整備）',
        place: 'both',
        why: '開催案内を届ける（④-7で配信設計）',
        auto: '手入力の通知／システム外メール等で送る',
      },
      {
        seq: 6,
        who: 'both',
        whoLabel: '参加者',
        when: '当日',
        where: '会場またはオンライン',
        what: '交流会を実施する',
        place: 'real',
        why: '交流そのもの',
      },
    ],
  },
  {
    id: 'success',
    title: '⑤ サクセス面談',
    lead: '日時未定で作成し、受講生が実施可能日から選んで確定します。出欠・課題・アンケートは使いません（タブも出ません）。',
    note: '「実施3週間前・2週間前」のリマインドは現状配信されません（⑤-10）。',
    steps: [
      {
        seq: 1,
        who: 'success',
        whoLabel: 'サクセス担当',
        when: '随時（事前）',
        where: '左メニュー → 実施可能日',
        what: '面談できる日時をまとめて登録する',
        place: 'screen',
        why: '受講生が選べる候補を用意する',
      },
      {
        seq: 2,
        who: 'office',
        whoLabel: '事務局',
        when: '対象月の前',
        where: 'イベント管理 → サクセス面談 → 「サクセス面談の新規登録」（または組み合わせCSV）',
        what: '受講生1名＋担当サクセスを指定し、日時未定のまま作成・公開',
        place: 'screen',
        why: '2人の連絡グループと日程選びの場を先に作る',
        auto: '日時未定のため開催○日前の予定は作れない。3週間前・2週間前は現状送られない（⑤-10）',
      },
      {
        seq: 3,
        who: 'student',
        whoLabel: '受講生',
        when: '作成・公開後',
        where: 'イベント詳細 → 候補日から選択',
        what: '実施可能日から1つ選んで日時を確定する',
        place: 'screen',
        why: '面談日程を確定し、以降のリマインドを組めるようにする',
        auto: '日時確定時に予定を展開。開催7日前09:00・受講生へ確認案内',
      },
      {
        seq: 4,
        who: 'office',
        whoLabel: '事務局',
        when: '（補助）',
        where: 'イベント詳細 → 候補日／概要の会議URL ／ Meet',
        what: '候補の追加・取消、Meet発行とURL登録',
        place: 'both',
        why: '調整を補佐し、接続情報を渡す',
        auto: '開催前日09:00・受講生へ前日案内',
      },
      {
        seq: 5,
        who: 'both',
        whoLabel: '両者',
        when: '当日',
        where: 'オンライン会議または対面',
        what: 'サクセス面談を実施する',
        place: 'real',
        why: '受講生の伴走・状況把握',
      },
      {
        seq: 6,
        who: 'success',
        whoLabel: 'サクセス担当',
        when: '面談後',
        where: 'イベント詳細 → 面談記録',
        what: '面談内容を記録する（閲覧はサクセス・事務局のみ）',
        place: 'screen',
        why: '引き継ぎと、後続の1on1担当決めの材料にする',
        visibility: 'サクセス担当と事務局のみ。理事・講師・受講生には見えない',
        auto: '毎週金曜15:00・事務局へ記録確認依頼（条件あり）',
      },
    ],
  },
  {
    id: 'oneonone',
    title: '⑥ 1on1',
    lead: '事務局が組み合わせを決め、日時未定で作成。候補日は事務局が代理登録します。',
    note: '「質問追いかけ」「アウトプット督促」は現状配信されません（⑥-8）。',
    steps: [
      {
        seq: 1,
        who: 'office',
        whoLabel: '事務局',
        when: 'サクセス面談後',
        where: 'サクセス面談のイベント詳細 → 面談記録 ／ 社内の判断',
        what: '記録を読み、理事・講師との組み合わせを決める（判断自体はシステム外）',
        place: 'both',
        why: '適切な1on1の相手を選ぶ',
      },
      {
        seq: 2,
        who: 'office',
        whoLabel: '事務局',
        when: '組合せ決定後',
        where: 'イベント管理 → 1on1 → 「1on1の新規登録」（または組み合わせCSV）',
        what: '受講生＋相手（理事・講師）を指定し、日時未定のまま作成・公開',
        place: 'screen',
        why: '2人のグループと日程調整の場を作る',
        auto: '日時未定のため開催○日前の予定は作れない。確定後に展開',
      },
      {
        seq: 3,
        who: 'office',
        whoLabel: '事務局',
        when: '作成時',
        where: '電話・メール等 ＋ イベント詳細 → 候補日の管理',
        what: '理事・講師から候補を聞き、代理で画面に登録する',
        place: 'both',
        why: '理事・講師にシステム入力を求めず候補をそろえる',
      },
      {
        seq: 4,
        who: 'student',
        whoLabel: '受講生',
        when: '随時',
        where: 'イベント詳細 → 候補日から選択',
        what: '候補から1つ選んで日時を確定する',
        place: 'screen',
        why: '実施日を確定し案内を組めるようにする',
        auto: '開催14日前09:00・確認案内／開催7日前09:00・開放案内（受講生）',
      },
      {
        seq: 5,
        who: 'office',
        whoLabel: '事務局',
        when: '日程確定前後',
        where: 'Meet本体 ＋ イベント詳細 → 会議URL',
        what: 'Meet発行・URL登録',
        place: 'both',
        why: '接続情報を受講生・理事講師に渡す',
      },
      {
        seq: 6,
        who: 'both',
        whoLabel: '受講生・理事講師',
        when: '当日',
        where: 'オンライン会議または対面',
        what: '1on1を実施する。質問事項の事前共有はグループ等で行う',
        place: 'both',
        why: '個別の伴走・相談',
      },
      {
        seq: 7,
        who: 'student',
        whoLabel: '受講生',
        when: '面談後（任意）',
        where: 'イベント詳細 → 事後アンケート（割当がある場合）',
        what: '事後アンケートがあれば回答する。専用アウトプット欄の要否は未確定',
        place: 'screen',
        why: '振り返りを残す（⑥-8）',
        auto: '質問追いかけ・アウトプット督促は現状送られない（面談に課題割当不可／⑥-8）',
      },
    ],
  },
];
